import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Truck, MapPin, ArrowRight, Wrench } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (orderId && token) {
      axios.get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setOrder(res.data))
        .catch(() => {});
    }
  }, [orderId, token]);

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px] container mx-auto px-4 md:px-6 max-w-3xl py-12">
        {/* Success header */}
        <div className="text-center mb-10" data-testid="order-success-section">
          <div className="w-20 h-20 bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-primary" />
          </div>
          <h1 className="font-heading font-black text-4xl uppercase text-white mb-2">Order Confirmed!</h1>
          <p className="text-muted-foreground text-base">Thank you for your order. We'll get it shipped to you shortly.</p>
          {order && (
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-4 py-2 mt-4">
              <span className="text-primary text-sm font-mono font-bold" data-testid="order-number">Order: {order.order_number}</span>
            </div>
          )}
        </div>

        {/* Order details */}
        {order && (
          <div className="bg-surface border border-white/5 p-6 mb-6">
            <h2 className="font-heading font-bold text-lg uppercase text-white mb-4">Order Details</h2>

            {/* Status */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 bg-yellow-400 animate-pulse" />
                <span className="text-yellow-400 font-bold capitalize">{order.status}</span>
              </div>
              <span className="text-muted-foreground text-xs">·</span>
              <span className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString()}</span>
            </div>

            {/* Timeline */}
            <div className="flex items-center justify-between mb-6 overflow-x-auto">
              {[
                { icon: CheckCircle, label: 'Confirmed', done: true },
                { icon: Package, label: 'Processing', done: false },
                { icon: Truck, label: 'Shipped', done: false },
                { icon: MapPin, label: 'Delivered', done: false },
              ].map(({ icon: Icon, label, done }, i) => (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center border-2 ${done ? 'bg-primary border-primary' : 'border-white/20 bg-background'}`}>
                      <Icon size={14} className={done ? 'text-black' : 'text-white/30'} />
                    </div>
                    <span className={`text-xs mt-1 ${done ? 'text-primary font-bold' : 'text-white/40'}`}>{label}</span>
                  </div>
                  {i < 3 && <div className={`w-12 md:w-20 h-px mx-1 ${done ? 'bg-primary/30' : 'bg-white/10'}`} />}
                </div>
              ))}
            </div>

            {/* Items */}
            <div className="space-y-3 mb-4">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5">
                  <img src={item.image} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-primary">{item.brand}</p>
                    <p className="text-sm text-white">{item.product_name}</p>
                    <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <span className="text-sm font-mono text-primary font-bold">AED {item.subtotal?.toFixed(0)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="bg-background p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-white font-mono">AED {order.subtotal?.toFixed(0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className={order.shipping_cost === 0 ? 'text-green-400' : 'text-white font-mono'}>
                  {order.shipping_cost === 0 ? 'FREE' : `AED ${order.shipping_cost}`}
                </span>
              </div>
              <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                <span className="text-white">Total</span>
                <span className="price-tag text-primary text-lg">AED {order.total?.toFixed(0)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Shipping info */}
        {order?.shipping_address && (
          <div className="bg-surface border border-white/5 p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-primary" />
              <h3 className="font-heading font-bold text-sm uppercase text-white">Delivery Address</h3>
            </div>
            <p className="text-sm text-white">{order.shipping_address.full_name}</p>
            <p className="text-sm text-white/60">{order.shipping_address.street}, {order.shipping_address.area}</p>
            <p className="text-sm text-white/60">{order.shipping_address.emirate}, {order.shipping_address.country}</p>
            <p className="text-sm text-white/60">{order.shipping_address.phone}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/products" className="flex-1 flex items-center justify-center gap-2 btn-primary text-sm">
            Continue Shopping <ArrowRight size={15} />
          </Link>
          <Link to="/dashboard" className="flex-1 flex items-center justify-center gap-2 btn-secondary text-sm">
            View All Orders
          </Link>
        </div>

        {/* Help note */}
        <div className="mt-8 p-4 bg-surface/50 border border-white/5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Wrench size={14} className="text-primary" />
            <span className="text-sm font-bold text-white">Need Help?</span>
          </div>
          <p className="text-xs text-muted-foreground">Contact us at accounts@toolshub.world or call +971 4 324 7147</p>
        </div>
      </div>
    </div>
  );
}
